import { Box, IconButton, Typography } from '@mui/material';
import { KeyboardBackspace } from '@mui/icons-material';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import { PATHS } from 'routes/constants';
import { searchPpwrJotformsRequest } from 'redux/reducers/ppwr/reducer';
import { getPpwrJotforms, getPpwrJotformsLoading } from 'redux/reducers/ppwr/selectors';
import { TPpwrJotformListItem } from 'redux/reducers/ppwr/types';
import { ACTIVATE_SERVICE_ACTION, tableStructure as defaultTableStructure } from './tableStructure';
import { ActivateServiceDialog } from '../ActivateServiceDialog';

export const PpwrRegistrationForms: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [selectedJotformId, setSelectedJotformId] = useState<number | null>(null);

	const list = useSelector(getPpwrJotforms);
	const listLoading = useSelector(getPpwrJotformsLoading);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TPpwrJotformListItem>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			searchPpwrJotformsRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAction = useCallback((id: number, action: string) => {
		if (action === ACTIVATE_SERVICE_ACTION) setSelectedJotformId(id);
	}, []);

	const handleDialogClose = useCallback(() => {
		setSelectedJotformId(null);
	}, []);

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<ActivateServiceDialog
				key={selectedJotformId ?? 'closed'}
				jotformId={selectedJotformId}
				onClose={handleDialogClose}
			/>
			<EasTableWithFilter<TPpwrJotformListItem>
				tableStructure={currentTableStructure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box
					sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
					onClick={() => navigate(PATHS.PPWR_PAGE)}
				>
					<IconButton color="primary" sx={{ p: 0 }}>
						<KeyboardBackspace />
					</IconButton>
					<Typography variant="body1" color="primary">
						Extra Services / EPR/PPWR / <b>Add New EPR/PPWR</b>
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
